import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { MARKETPLACE_ENABLED } from '../config/featureFlags';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useCustomerSelection } from '../context/CustomerSelectionContext';
import { marketplaceService } from '../services/marketplaceService';
import { preloadIcons } from '../services/iconCache';
import { CustomerMicrosoftLicencePricing } from '../PartnerMarketplace/MicrosoftLicencesTab/CustomerMicrosoftLicencePricing';
import { termPricedLabel } from '../PartnerMarketplace/vendorPresets';

/**
 * Pre-scoped Dicker Data SKUs for initial sync.
 * 
 * These are the product codes that will be synced from Dicker Data when partners
 * trigger a sync. Add SKUs here to include them in the daily refresh.
 * 
 * Format: { sku: 'PART_NUMBER', category: 'CategoryName', name: 'Display Name' }
 * 
 * Categories: 'Computers', 'Networking', 'Mobile', 'Software', 'Other'
 */
export const SEED_SKUS = [
  // === LAPTOPS ===
  // HP EliteBook series
  { sku: 'B9GC5PT', category: 'Computers', name: 'HP EliteBook 840 G9' },
  
  // === NETWORKING ===
  // Add networking SKUs here
  // { sku: 'XXXXXX', category: 'Networking', name: 'Cisco Catalyst 1000' },
  
  // === MOBILE ===
  // Add mobile device SKUs here
  // { sku: 'XXXXXX', category: 'Mobile', name: 'iPhone 15 Pro' },
  
  // === ADD MORE SKUS BELOW ===
  // Uncomment and modify the examples above, or add new entries:
  // { sku: 'PART_NUMBER', category: 'Category', name: 'Product Name' },
];

/**
 * Get enabled seed SKUs for API sync.
 * This is called by the marketplace function to determine which products to fetch.
 */
export function getEnabledSeedSkus() {
  return SEED_SKUS.filter(s => s.sku && s.sku.trim().length > 0);
}

// Marketplace types (matching aitryx-customer interfaces)
export interface MarketplaceSoftware {
  id: string;
  name: string;
  vendor: string;
  category: string;
  price: number;
  pricingType: 'perUser' | 'lumpSum';
  term: 6 | 12 | 18 | 24 | 36;
  billingCycle: 'monthly' | 'annual';
  description: string;
  image?: string;
  colour?: string;
  capacity?: string;
  popular?: boolean;
  enabled?: boolean;
  // Hidden routing tag carried from partner snapshot (not displayed)
  orderTag?: 'hardware_orders___returns' | 'software_orders' | 'other_orders';
  vendorPreset?: string;
  termPricing?: Record<string, number>;
}

export interface MarketplaceHardware {
  id: string;
  name: string;      // Changed from 'model' to match customer expectation
  brand: string;     // Changed from 'manufacturer' to match customer expectation
  category: string;
  price: number;
  pricingType: 'perUser' | 'lumpSum';
  term: 6 | 12 | 18 | 24 | 36;
  billingCycle: 'monthly' | 'annual';
  description: string;
  specs: string[];
  image?: string;
  colour?: string;
  capacity?: string;
  popular?: boolean;
  enabled?: boolean;
  // Hidden routing tag carried from partner snapshot (not displayed)
  orderTag?: 'hardware_orders___returns' | 'software_orders' | 'other_orders';
}

export interface MarketplaceConfig {
  software: MarketplaceSoftware[];
  hardware: MarketplaceHardware[];
  updatedAt: string;
  updatedBy: string;
  version: string;
  isCustomized: boolean;
}

type CustomerCatalog = Awaited<ReturnType<typeof marketplaceService.getCustomerAssignedCatalog>>;

/** The customer's catalogue from the marketplace function, in the shape this view displays. */
function toMarketplaceConfig(customerCatalog: CustomerCatalog): MarketplaceConfig {
  if (!customerCatalog) {
    return {
      software: [],
      hardware: [],
      updatedAt: new Date().toISOString(),
      updatedBy: 'system',
      version: '1.0',
      isCustomized: false,
    };
  }

  return {
    software: (customerCatalog.software || []).map((item: any) => ({
      id: item.id || item.Id,
      name: item.name || item.Name || 'Unnamed',
      vendor: item.brand || item.Brand || item.vendor || 'Unknown',
      category: item.category || item.Category || 'General',
      price: item.price || item.Price || 0,
      pricingType: item.pricingType || 'perUser',
      term: item.term ?? 12,
      billingCycle: item.billingCycle || 'monthly',
      description: item.description || item.Description || '',
      image: item.image,
      popular: item.popular || false,
      enabled: item.enabled !== false,
      vendorPreset: item.vendorPreset,
      termPricing: item.termPricing,
    })),
    hardware: [
      // Include hardware items
      ...(customerCatalog.hardware || []).map((item: any) => ({
        id: item.id || item.Id,
        name: item.name || item.Name || 'Unnamed',
        brand: item.brand || item.Brand || 'Unknown',
        category: item.category || item.Category || 'computers',
        price: item.price || item.Price || 0,
        pricingType: item.pricingType || 'lumpSum',
        term: item.term ?? 12,
        billingCycle: item.billingCycle || 'monthly',
        description: item.description || item.Description || '',
        specs: item.specs || [],
        image: item.image,
        popular: item.popular || false,
        enabled: item.enabled !== false,
      })),
      // Include consulting items (displayed as hardware/services)
      ...(customerCatalog.consulting || []).map((item: any) => ({
        id: item.id || item.Id,
        name: item.name || item.Name || 'Unnamed',
        brand: item.brand || item.Brand || 'Service Provider',
        category: item.category || item.Category || 'consulting',
        price: item.price || item.Price || 0,
        pricingType: item.pricingType || 'lumpSum',
        term: item.term ?? 12,
        billingCycle: item.billingCycle || 'monthly',
        description: item.description || item.Description || '',
        specs: item.specs || [],
        image: item.image,
        popular: item.popular || false,
        enabled: item.enabled !== false,
      })),
    ],
    updatedAt: customerCatalog.updatedAt || new Date().toISOString(),
    updatedBy: 'partner',
    version: '1.0',
    isCustomized: false,
  };
}

export function MarketplaceView() {
  const { partner } = useAuth();
  const { selectedCustomer } = useCustomerSelection();
  const [marketplaceConfig, setMarketplaceConfig] = useState<MarketplaceConfig | null>(null);
  const [availableSnapshots, setAvailableSnapshots] = useState<Array<{ snapshotId: string; name: string; createdAtUtc: string; itemCount: number }>>([]);
  // Single assigned catalogue (simplified from multi-select)
  const [assignedCatalogueId, setAssignedCatalogueId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editCataloguesModalOpen, setEditCataloguesModalOpen] = useState(false);
  // Single selection for the dropdown
  const [selectedCatalogueId, setSelectedCatalogueId] = useState<string | null>(null);
  const [savingCatalogues, setSavingCatalogues] = useState(false);
  
  // Image URL cache for pack: URL resolution
  const [imageUrlCache, setImageUrlCache] = useState<Record<string, string>>({});
  const [unassigning, setUnassigning] = useState(false);

  /** Partner-configurable computer onboarding (per unit AUD); persisted on customer catalog blob */
  const [onboardingChargeAudStr, setOnboardingChargeAudStr] = useState('');
  const [onboardingLabelStr, setOnboardingLabelStr] = useState('');
  const [onboardingSaving, setOnboardingSaving] = useState(false);

  /** Telstra handset settings — Device Enrolment Service toggle (per customer). */
  const [desAvailable, setDesAvailable] = useState(false);
  const [microsoftLicencesEnabled, setMicrosoftLicencesEnabled] = useState(true);
  const [microsoftLicencesSaving, setMicrosoftLicencesSaving] = useState(false);
  const [desSaving, setDesSaving] = useState(false);
  
  // Helper to get cached image URL
  const getCachedImageUrl = useCallback((imageUrl: string | undefined): string | undefined => {
    if (!imageUrl) return undefined;
    
    if (imageUrl.startsWith('http') || imageUrl.startsWith('pack:')) {
      const cached = imageUrlCache[imageUrl];
      if (cached) return cached;
      
      // For pack: URLs, return undefined if not cached (prevents CSP errors)
      if (imageUrl.startsWith('pack:')) {
        return undefined;
      }
      
      return imageUrl;
    }
    
    return imageUrl;
  }, [imageUrlCache]);

  // Load marketplace configuration and available catalogues for selected customer
  const loadMarketplaceData = useCallback(async (customerId: string) => {
    if (!partner?.partnerId || !customerId) return;

    setIsLoading(true);
    setError(null);
    try {
      // Load available partner catalogues first
      const snapshots = await marketplaceService.listPartnerSnapshotsBlob();
      setAvailableSnapshots(snapshots || []);

      // Get the customer's assigned catalogue from the marketplace function
      // This returns the full catalog with products from {customerId}/marketplace/catalog.json
      const customerCatalog = await marketplaceService.getCustomerAssignedCatalog(customerId);
      
      if (customerCatalog) {
        const rawAud = (customerCatalog as { computerOnboardingChargeAud?: number | null }).computerOnboardingChargeAud;
        setOnboardingChargeAudStr(
          rawAud != null && typeof rawAud === 'number' && !Number.isNaN(rawAud)
            ? String(rawAud)
            : ''
        );
        setOnboardingLabelStr(
          (customerCatalog as { computerOnboardingChargeLabel?: string | null }).computerOnboardingChargeLabel?.trim() ?? ''
        );
        setDesAvailable(
          (customerCatalog as { deviceEnrolmentAvailable?: boolean }).deviceEnrolmentAvailable === true
        );
        setMicrosoftLicencesEnabled(
          (customerCatalog as { microsoftLicencesEnabled?: boolean | null }).microsoftLicencesEnabled !== false
        );
        setAssignedCatalogueId(customerCatalog.assignedSnapshotId || null);
        setSelectedCatalogueId(customerCatalog.assignedSnapshotId || null);
      } else {
        setOnboardingChargeAudStr('');
        setOnboardingLabelStr('');
        setDesAvailable(false);
        setMicrosoftLicencesEnabled(true);
        setAssignedCatalogueId(null);
        setSelectedCatalogueId(null);
      }
      setMarketplaceConfig(toMarketplaceConfig(customerCatalog));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load marketplace data');
    } finally {
      setIsLoading(false);
    }
  }, [partner?.partnerId]);

  const selectedCustomerIdRef = useRef(selectedCustomer?.customerId);
  selectedCustomerIdRef.current = selectedCustomer?.customerId;

  // Products only: loadMarketplaceData swaps the page for its spinner, which remounts the
  // Microsoft licence pricing panel and drops its saved message.
  const refreshCustomerProducts = useCallback(async (customerId: string) => {
    try {
      const customerCatalog = await marketplaceService.getCustomerAssignedCatalog(customerId);
      if (selectedCustomerIdRef.current === customerId) {
        setMarketplaceConfig(toMarketplaceConfig(customerCatalog));
      }
    } catch (err) {
      if (selectedCustomerIdRef.current === customerId) {
        setError(err instanceof Error ? err.message : "Couldn't reload this customer's products");
      }
    }
  }, []);

  // Open Edit Catalogues modal
  const openEditCataloguesModal = () => {
    if (!MARKETPLACE_ENABLED) {
      return;
    }
    // Pre-select the currently assigned catalogue
    setSelectedCatalogueId(assignedCatalogueId);
    setEditCataloguesModalOpen(true);
  };

  // Handle catalogue selection change in dropdown
  const handleCatalogueSelectionChange = (snapshotId: string | null) => {
    setSelectedCatalogueId(snapshotId);
  };

  // Save catalogue assignment (single catalogue)
  const saveCatalogueAssignment = async () => {
    if (!MARKETPLACE_ENABLED) {
      return;
    }
    if (!partner?.partnerId || !selectedCustomer?.customerId) return;

    // If selection hasn't changed, just close the modal
    if (selectedCatalogueId === assignedCatalogueId) {
      setEditCataloguesModalOpen(false);
      return;
    }

    setSavingCatalogues(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (selectedCatalogueId) {
        // Assign the selected catalogue to the customer
        // This calls the backend which saves to customer blob and triggers Event Relay
        await marketplaceService.assignCatalogToCustomer(selectedCatalogueId, selectedCustomer.customerId);
        
        const selectedSnapshot = availableSnapshots.find(s => s.snapshotId === selectedCatalogueId);
        setSuccessMessage(`Successfully assigned "${selectedSnapshot?.name || 'catalogue'}" to ${selectedCustomer.customerName}`);
      } else {
        // No catalogue selected - this could mean clearing the assignment
        // For now, we just close without action since unassign isn't supported
        setSuccessMessage(`Catalogue assignment unchanged for ${selectedCustomer.customerName}`);
      }

      // Reload data to reflect the new assignment
      await loadMarketplaceData(selectedCustomer.customerId);
      setEditCataloguesModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign catalogue');
    } finally {
      setSavingCatalogues(false);
    }
  };

  // Find or create the "No Catalogue" empty catalogue
  const getOrCreateNoCatalogue = async (): Promise<string | null> => {
    try {
      // Check if "No Catalogue" already exists
      const snapshots = await marketplaceService.listPartnerSnapshotsBlob();
      const noCatalogue = snapshots?.find(s => s.name === 'No Catalogue');
      
      if (noCatalogue) {
        return noCatalogue.snapshotId;
      }
      
      // Create "No Catalogue" if it doesn't exist
      await marketplaceService.createPartnerSnapshotBlob({
        name: 'No Catalogue',
        notes: 'Empty catalogue used for customers with no product access',
        software: [],
        hardware: [],
        consulting: []
      });
      
      // Get the newly created catalogue
      const updatedSnapshots = await marketplaceService.listPartnerSnapshotsBlob();
      const newNoCatalogue = updatedSnapshots?.find(s => s.name === 'No Catalogue');
      return newNoCatalogue?.snapshotId || null;
    } catch (err) {
      return null;
    }
  };

  // Unassign catalogue from customer (assigns empty "No Catalogue" instead)
  const unassignCatalogue = async () => {
    if (!MARKETPLACE_ENABLED) {
      return;
    }
    if (!partner?.partnerId || !selectedCustomer?.customerId) return;
    if (!assignedCatalogueId) return;

    // Confirm before unassigning
    if (!window.confirm(`Are you sure you want to unassign the catalogue from ${selectedCustomer.customerName}? They will no longer see any products in their marketplace.`)) {
      return;
    }

    setUnassigning(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Get or create the "No Catalogue" empty catalogue
      const noCatalogueId = await getOrCreateNoCatalogue();
      
      if (!noCatalogueId) {
        throw new Error('Failed to find or create empty catalogue');
      }
      
      // Assign the empty catalogue to the customer
      await marketplaceService.assignCatalogToCustomer(noCatalogueId, selectedCustomer.customerId);
      
      setSuccessMessage(`Successfully unassigned catalogue from ${selectedCustomer.customerName}`);
      
      // Reload data to reflect the removal
      await loadMarketplaceData(selectedCustomer.customerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unassign catalogue');
    } finally {
      setUnassigning(false);
    }
  };

  // Load marketplace data when customer selection changes
  useEffect(() => {
    if (selectedCustomer?.customerId) {
      loadMarketplaceData(selectedCustomer.customerId);
    } else {
      setMarketplaceConfig(null);
      setAvailableSnapshots([]);
    }
  }, [selectedCustomer?.customerId, loadMarketplaceData]);

  // Preload and cache images when marketplace config changes
  useEffect(() => {
    const cacheImages = async () => {
      if (!marketplaceConfig) return;
      
      const imageUrls: string[] = [];
      
      // Collect all image URLs
      marketplaceConfig.software.forEach(item => {
        if (item.image && (item.image.startsWith('http') || item.image.startsWith('pack:'))) {
          imageUrls.push(item.image);
        }
      });
      marketplaceConfig.hardware.forEach(item => {
        if (item.image && (item.image.startsWith('http') || item.image.startsWith('pack:'))) {
          imageUrls.push(item.image);
        }
      });
      
      if (imageUrls.length > 0) {
        try {
          const cached = await preloadIcons(imageUrls);
          setImageUrlCache(prev => ({ ...prev, ...cached }));
        } catch (error) {
          // Image preloading is best-effort
        }
      }
    };
    
    cacheImages();
  }, [marketplaceConfig]);

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Get the assigned catalogue name for display
  const assignedCatalogueName = assignedCatalogueId 
    ? availableSnapshots.find(s => s.snapshotId === assignedCatalogueId)?.name || 'Unknown'
    : null;

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [productFilter, setProductFilter] = useState<'all' | 'Software' | 'Hardware'>('all');
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);

  // Build product list with full details for expansion
  const allProducts = useMemo(() => {
    const products: Array<{
      id: string;
      name: string;
      vendorOrBrand: string;
      type: 'Software' | 'Hardware';
      category: string;
      price: number;
      priceDisplay: string;
      pricingType: string;
      description: string;
      specs?: string[];
      image?: string;
    }> = [];

    if (marketplaceConfig?.software) {
      for (const s of marketplaceConfig.software) {
        const price = typeof s.price === 'number' ? s.price : 0;
        let priceDisplay: string;
        if (s.termPricing && Object.keys(s.termPricing).length > 0) {
          priceDisplay = termPricedLabel(price, s.termPricing);
        } else {
          priceDisplay = price > 0 ? (s.pricingType === 'perUser' ? `$${price.toLocaleString()}/user` : `$${price.toLocaleString()}`) : 'Contact for pricing';
        }
        products.push({
          id: s.id,
          name: s.name,
          vendorOrBrand: s.vendor,
          type: 'Software',
          category: s.category || 'General',
          price,
          priceDisplay,
          pricingType: s.pricingType || 'perUser',
          description: s.description || 'No description available.',
          image: s.image,
        });
      }
    }
    if (marketplaceConfig?.hardware) {
      for (const h of marketplaceConfig.hardware) {
        const price = typeof h.price === 'number' ? h.price : 0;
        products.push({
          id: h.id,
          name: h.name,
          vendorOrBrand: h.brand,
          type: 'Hardware',
          category: h.category || 'computers',
          price,
          priceDisplay: price > 0 ? `$${price.toLocaleString()}` : 'Contact for pricing',
          pricingType: h.pricingType || 'lumpSum',
          description: h.description || 'No description available.',
          specs: h.specs,
          image: h.image,
        });
      }
    }

    return products;
  }, [marketplaceConfig?.software, marketplaceConfig?.hardware]);

  // Filter and search products
  const filteredProducts = useMemo(() => {
    let result = allProducts;

    // Apply type filter
    if (productFilter !== 'all') {
      result = result.filter(p => p.type === productFilter);
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.vendorOrBrand.toLowerCase().includes(query) ||
        p.category.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
    }

    // Sort by type then name
    return result.sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
  }, [allProducts, productFilter, searchQuery]);

  // Counts for filter pills
  const softwareCount = allProducts.filter(p => p.type === 'Software').length;
  const hardwareCount = allProducts.filter(p => p.type === 'Hardware').length;

  // Navigate to edit catalogue
  const navigateToEditCatalogue = () => {
    // Navigate to the Marketplace tab where catalogues are managed
    window.location.href = '/marketplace';
  };

  const saveComputerOnboarding = async () => {
    if (!MARKETPLACE_ENABLED || !selectedCustomer?.customerId) return;
    const trimmed = onboardingChargeAudStr.trim();
    let amount: number | null = null;
    if (trimmed !== '') {
      const n = parseFloat(trimmed);
      if (Number.isNaN(n) || n < 0) {
        setError('Enter a valid non-negative amount (AUD per computer), or leave empty to disable.');
        return;
      }
      amount = n;
    }
    setOnboardingSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await marketplaceService.updateCustomerComputerOnboarding(selectedCustomer.customerId, {
        computerOnboardingChargeAud: amount,
        computerOnboardingChargeLabel: onboardingLabelStr.trim() || null,
      });
      setSuccessMessage('Computer onboarding settings saved.');
      await loadMarketplaceData(selectedCustomer.customerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save computer onboarding');
    } finally {
      setOnboardingSaving(false);
    }
  };

  const toggleMicrosoftLicences = async (nextValue: boolean) => {
    if (!MARKETPLACE_ENABLED || !selectedCustomer?.customerId) return;
    const previous = microsoftLicencesEnabled;
    setMicrosoftLicencesEnabled(nextValue);
    setMicrosoftLicencesSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await marketplaceService.updateCustomerMicrosoftLicences(selectedCustomer.customerId, nextValue);
      setSuccessMessage(nextValue
        ? 'Microsoft licences are visible in this customer’s software catalogue.'
        : 'Microsoft licences are hidden for this customer.');
      await loadMarketplaceData(selectedCustomer.customerId);
    } catch (err) {
      setMicrosoftLicencesEnabled(previous);
      setError(err instanceof Error ? err.message : 'Failed to update Microsoft licences');
    } finally {
      setMicrosoftLicencesSaving(false);
    }
  };

  const toggleDeviceEnrolment = async (nextValue: boolean) => {
    if (!MARKETPLACE_ENABLED || !selectedCustomer?.customerId) return;
    const previous = desAvailable;
    setDesAvailable(nextValue); // optimistic
    setDesSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await marketplaceService.updateCustomerMobileHardwareSettings(selectedCustomer.customerId, {
        deviceEnrolmentAvailable: nextValue,
      });
      setSuccessMessage(nextValue ? 'Device Enrolment enabled for this customer.' : 'Device Enrolment disabled for this customer.');
    } catch (err) {
      setDesAvailable(previous); // revert on failure
      setError(err instanceof Error ? err.message : 'Failed to save Device Enrolment setting');
    } finally {
      setDesSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {!MARKETPLACE_ENABLED && (
          <div className="mx-6 mt-6 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm">
            Marketplace actions are disabled for V1. UI is read-only.
          </div>
        )}
        
        {!selectedCustomer ? (
          <div className="p-6">
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Customer Selected</h3>
              <p className="text-slate-500">Select a customer from the sidebar to view their marketplace configuration.</p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Error/Success Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            )}

            {successMessage && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-emerald-800 text-sm">{successMessage}</span>
              </div>
            )}

            {/* Loading State */}
            {isLoading ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading marketplace configuration...</p>
              </div>
            ) : (
              <>
                {/* Header Card */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <div>
                            <h1 className="text-xl font-bold text-slate-900">{selectedCustomer.customerName}</h1>
                            <p className="text-sm text-slate-500">Marketplace Configuration</p>
                          </div>
                        </div>
                        
                        {/* Catalogue Badge */}
                        {assignedCatalogueName ? (
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-blue-900">{assignedCatalogueName}</span>
                            <span className="text-xs text-blue-600">•</span>
                            <span className="text-xs text-blue-700">{allProducts.length} products</span>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span className="text-sm font-medium text-amber-900">No catalogue assigned</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        {assignedCatalogueId && (
                          <>
                            <Button
                              variant="outline"
                              onClick={navigateToEditCatalogue}
                              size="sm"
                              className="text-slate-700 border-slate-300 hover:bg-slate-50"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit Catalogue
                            </Button>
                            <Button
                              variant="outline"
                              onClick={unassignCatalogue}
                              size="sm"
                              disabled={!MARKETPLACE_ENABLED || unassigning}
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                              {unassigning ? 'Unassigning...' : 'Unassign'}
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={openEditCataloguesModal}
                          size="sm"
                          disabled={!MARKETPLACE_ENABLED}
                          className="bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          {assignedCatalogueId ? 'Change Catalogue' : 'Assign Catalogue'}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats Bar */}
                  <div className="border-t border-slate-100 bg-slate-50/50 px-6 py-3 flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                      <span className="text-sm text-slate-600">{softwareCount} Software</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-sm text-slate-600">{hardwareCount} Hardware</span>
                    </div>
                  </div>
                </div>

                {/* Computer onboarding — per customer; survives catalogue reassign when merged server-side */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-sm font-semibold text-slate-900 mb-1">Computer onboarding charge</h2>
                  <p className="text-xs text-slate-500 mb-4 max-w-2xl">
                    Per computer unit (AUD). Applied when this customer checks out with at least one computer in the cart;
                    multiplied by computer quantity. Leave amount empty to disable.
                  </p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-end">
                    <div className="flex-1 min-w-[140px]">
                      <label htmlFor="onboarding-aud" className="block text-xs font-medium text-slate-700 mb-1">
                        Amount (AUD per unit)
                      </label>
                      <input
                        id="onboarding-aud"
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="e.g. 150"
                        value={onboardingChargeAudStr}
                        onChange={(e) => setOnboardingChargeAudStr(e.target.value)}
                        disabled={!MARKETPLACE_ENABLED || onboardingSaving}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                      />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <label htmlFor="onboarding-label" className="block text-xs font-medium text-slate-700 mb-1">
                        Label (optional)
                      </label>
                      <input
                        id="onboarding-label"
                        type="text"
                        placeholder="e.g. Computer onboarding"
                        value={onboardingLabelStr}
                        onChange={(e) => setOnboardingLabelStr(e.target.value)}
                        disabled={!MARKETPLACE_ENABLED || onboardingSaving}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={saveComputerOnboarding}
                      disabled={!MARKETPLACE_ENABLED || onboardingSaving}
                      size="sm"
                      className="bg-slate-900 text-white hover:bg-slate-800 sm:self-end"
                    >
                      {onboardingSaving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-sm font-semibold text-slate-900 mb-1">Microsoft licences</h2>
                  <p className="text-xs text-slate-500 mb-4 max-w-2xl">
                    On by default. This customer sees every Microsoft licence that has a price under Software, and can buy
                    another seat from the New User request. Turn this off to hide them for this customer only.
                  </p>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">Show Microsoft licences</div>
                      <p className="text-xs text-slate-500 mt-1 max-w-xl">
                        Prices come from your distributor price lists under Marketplace, Microsoft licences, unless you
                        set a distributor or a price for this customer below.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={microsoftLicencesEnabled}
                      aria-label="Show Microsoft licences"
                      disabled={!MARKETPLACE_ENABLED || microsoftLicencesSaving}
                      onClick={() => toggleMicrosoftLicences(!microsoftLicencesEnabled)}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                        microsoftLicencesEnabled ? 'bg-blue-600' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          microsoftLicencesEnabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  {MARKETPLACE_ENABLED && selectedCustomer?.customerId && (
                    <div className="mt-6 border-t border-slate-100 pt-5">
                      <CustomerMicrosoftLicencePricing
                        key={selectedCustomer.customerId}
                        customerId={selectedCustomer.customerId}
                        customerName={selectedCustomer.customerName}
                        licencesShown={microsoftLicencesEnabled}
                        onSaved={() => refreshCustomerProducts(selectedCustomer.customerId)}
                      />
                    </div>
                  )}
                </div>

                {/* Telstra handset settings — per-customer Device Enrolment Service toggle */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-sm font-semibold text-slate-900 mb-1">Telstra handset settings</h2>
                  <p className="text-xs text-slate-500 mb-4 max-w-2xl">
                    Controls options the customer sees when ordering Telstra handsets from their marketplace.
                  </p>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">Device Enrolment Available</div>
                      <p className="text-xs text-slate-500 mt-1 max-w-xl">
                        When on, customers see a "Enrol this device in Device Enrolment Service?" prompt in the procurement
                        flow (default Yes). The selection is included in the resulting ticket.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={desAvailable}
                      aria-label="Device Enrolment Available"
                      disabled={!MARKETPLACE_ENABLED || desSaving}
                      onClick={() => toggleDeviceEnrolment(!desAvailable)}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                        desAvailable ? 'bg-blue-600' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          desAvailable ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Products Section */}
                {allProducts.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    {/* Search and Filter Bar */}
                    <div className="p-4 border-b border-slate-100">
                      <div className="flex items-center gap-4">
                        {/* Search */}
                        <div className="flex-1 relative">
                          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <input
                            type="text"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          {searchQuery && (
                            <button
                              onClick={() => setSearchQuery('')}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              title="Clear search"
                              aria-label="Clear search"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                        
                        {/* Filter Pills */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setProductFilter('all')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                              productFilter === 'all'
                                ? 'bg-slate-900 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            All ({allProducts.length})
                          </button>
                          <button
                            onClick={() => setProductFilter('Software')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                              productFilter === 'Software'
                                ? 'bg-purple-600 text-white'
                                : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                            }`}
                          >
                            Software ({softwareCount})
                          </button>
                          <button
                            onClick={() => setProductFilter('Hardware')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                              productFilter === 'Hardware'
                                ? 'bg-emerald-600 text-white'
                                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            }`}
                          >
                            Hardware ({hardwareCount})
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Products List */}
                    <div className="divide-y divide-slate-100">
                      {filteredProducts.length === 0 ? (
                        <div className="p-8 text-center">
                          <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p className="text-slate-500">No products match your search</p>
                          <button
                            onClick={() => { setSearchQuery(''); setProductFilter('all'); }}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                          >
                            Clear filters
                          </button>
                        </div>
                      ) : (
                        filteredProducts.map((product) => (
                          <div key={product.id} className="group">
                            {/* Product Row */}
                            <button
                              onClick={() => setExpandedProductId(expandedProductId === product.id ? null : product.id)}
                              className="w-full px-4 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left"
                            >
                              {/* Type Badge */}
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                product.type === 'Software' ? 'bg-purple-500' : 'bg-emerald-500'
                              }`} />
                              
                              {/* Product Image or Icon */}
                              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {getCachedImageUrl(product.image) ? (
                                  <img src={getCachedImageUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
                                ) : (
                                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {product.type === 'Software' ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                    )}
                                  </svg>
                                )}
                              </div>
                              
                              {/* Product Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-900 truncate">{product.name}</span>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    product.type === 'Software' 
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'bg-emerald-100 text-emerald-700'
                                  }`}>
                                    {product.type}
                                  </span>
                                </div>
                                <div className="text-sm text-slate-500 truncate">{product.vendorOrBrand}</div>
                              </div>
                              
                              {/* Price */}
                              <div className="text-right flex-shrink-0">
                                <div className="font-semibold text-slate-900">{product.priceDisplay}</div>
                                <div className="text-xs text-slate-500 capitalize">{product.pricingType}</div>
                              </div>
                              
                              {/* Expand Icon */}
                              <svg 
                                className={`w-5 h-5 text-slate-400 transition-transform ${expandedProductId === product.id ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Expanded Details */}
                            {expandedProductId === product.id && (
                              <div className="px-4 pb-4 pt-0 bg-slate-50 border-t border-slate-100">
                                <div className="ml-16 space-y-3">
                                  {/* Category */}
                                  <div className="flex items-start gap-4">
                                    <span className="text-xs font-medium text-slate-500 w-20">Category</span>
                                    <span className="text-sm text-slate-700 capitalize">{product.category}</span>
                                  </div>
                                  
                                  {/* Description */}
                                  <div className="flex items-start gap-4">
                                    <span className="text-xs font-medium text-slate-500 w-20">Description</span>
                                    <span className="text-sm text-slate-700">{product.description}</span>
                                  </div>
                                  
                                  {/* Specs (for hardware) */}
                                  {product.specs && product.specs.length > 0 && (
                                    <div className="flex items-start gap-4">
                                      <span className="text-xs font-medium text-slate-500 w-20">Specs</span>
                                      <div className="flex flex-wrap gap-1">
                                        {product.specs.map((spec, i) => (
                                          <span key={i} className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs rounded">
                                            {spec}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Footer */}
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-center">
                      <p className="text-xs text-slate-500">
                        Showing {filteredProducts.length} of {allProducts.length} products
                        {searchQuery && ` matching "${searchQuery}"`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Empty State - No Products */}
                {allProducts.length === 0 && assignedCatalogueId && (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Catalogue is Empty</h3>
                    <p className="text-slate-500 mb-4">The assigned catalogue doesn't have any products yet.</p>
                    <Button variant="outline" onClick={navigateToEditCatalogue}>
                      Add Products to Catalogue
                    </Button>
                  </div>
                )}

                {/* Empty State - No Assignment */}
                {allProducts.length === 0 && !assignedCatalogueId && (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Catalogue Assigned</h3>
                    <p className="text-slate-500 mb-4">Assign a catalogue to show products to this customer.</p>
                    <Button onClick={openEditCataloguesModal} disabled={!MARKETPLACE_ENABLED}>
                      Assign Catalogue
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Assign Catalogue Modal */}
            {editCataloguesModalOpen && (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                  {/* Header */}
                  <div className="p-5 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900">
                        {assignedCatalogueId ? 'Change' : 'Assign'} Catalogue
                      </h3>
                      <button 
                        className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-200 rounded-full transition-colors" 
                        onClick={() => setEditCataloguesModalOpen(false)}
                        title="Close modal"
                        aria-label="Close modal"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      Select a catalogue to assign to {selectedCustomer?.customerName}
                    </p>
                  </div>

                  {/* Catalogue Selection */}
                  <div className="p-5">
                    {availableSnapshots.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <p>No catalogues available</p>
                        <p className="text-sm mt-1">Create catalogues in the Partner Marketplace first</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <label className="block">
                          <span className="text-sm font-medium text-slate-700">Select Catalogue</span>
                          <select
                            value={selectedCatalogueId || ''}
                            onChange={(e) => handleCatalogueSelectionChange(e.target.value || null)}
                            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            aria-label="Select catalogue to assign"
                          >
                            <option value="">-- Select a catalogue --</option>
                            {availableSnapshots.map((catalogue) => (
                              <option key={catalogue.snapshotId} value={catalogue.snapshotId}>
                                {catalogue.name} ({catalogue.itemCount} products)
                              </option>
                            ))}
                          </select>
                        </label>

                        {/* Selected catalogue preview */}
                        {selectedCatalogueId && (
                          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-sm font-medium text-blue-900">
                                {availableSnapshots.find(s => s.snapshotId === selectedCatalogueId)?.name}
                              </span>
                            </div>
                            <p className="text-xs text-blue-700 mt-1">
                              This catalogue will be pushed to the customer and they will see it immediately.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl flex items-center justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setEditCataloguesModalOpen(false)}
                      disabled={savingCatalogues}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={saveCatalogueAssignment}
                      disabled={savingCatalogues || !MARKETPLACE_ENABLED || !selectedCatalogueId}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                      title={!MARKETPLACE_ENABLED ? 'Disabled for V1' : !selectedCatalogueId ? 'Select a catalogue first' : undefined}
                    >
                      {savingCatalogues ? 'Assigning...' : 'Assign Catalogue'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
